import { useEffect, useState } from "react";
import { ListPlus, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { AddToPlaylistItems } from "@/components/TrackTable";
import { REMOVE_NOTE } from "@/components/SelectionBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

/* Preview what a removal touches, confirm, then remove. Returns the server result or null when cancelled. */
export const removeWithPreview = async (body, what) => {
  const { data: p } = await api.post("/library/remove", { ...body, dry_run: true });
  if (!p.tracks) { toast.info("Nothing to remove"); return null; }
  const parts = [body.artists?.length ? plural(p.artists, "artist") : null, plural(p.albums, "album"), plural(p.tracks, "song")].filter(Boolean);
  if (!window.confirm(`Remove ${what} from your library?\n\nThis removes ${parts.join(" · ")}.\n\n${REMOVE_NOTE}`)) return null;
  const { data } = await api.post("/library/remove", body);
  toast.success(`Removed ${plural(data.tracks, "song")}${data.albums ? ` · ${plural(data.albums, "album")}` : ""}`);
  return data;
};

/* "Add to…" (queue / playlist) for a selection of albums or artists; tracks are fetched when the menu opens. */
export const AddSelectionTo = ({ body, testid = "selection-add-to" }) => {
  const p = usePlayer();
  const [tracks, setTracks] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(body);
  useEffect(() => { setTracks(null); }, [key]);
  const load = async (open) => {
    if (!open || tracks) return;
    setLoading(true);
    try { const { data } = await api.post("/library/tracks-of", body); setTracks(data.tracks); } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  return (
    <DropdownMenu onOpenChange={load}>
      <DropdownMenuTrigger data-testid={testid} className="flex h-9 items-center gap-1.5 rounded-full px-3 text-xs text-zinc-600 hover:bg-zinc-100"><ListPlus size={14} />Add to…</DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-52">
        {loading || !tracks ? <DropdownMenuItem disabled><Loader2 size={14} className="mr-2 animate-spin" />Loading songs…</DropdownMenuItem> : (
          <>
            <DropdownMenuItem onClick={() => { p.addToQueue(tracks); toast.success(`Added ${plural(tracks.length, "song")} to queue`); }} data-testid={`${testid}-queue`}><Plus size={14} className="mr-2" />Queue</DropdownMenuItem>
            <AddToPlaylistItems trackIds={tracks.map((t) => t.id)} />
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
